import moment from "moment";
import { formatDate, formatNumber, toUtcDay, formatUTCDate, getPreviousDayForGivenDate, getNextDay, firstBusinessDayOfNextYear, formatDecimalPlace, formatTodaysDate } from "common/formatters";
import TypedSelect from "common/ui/TypedSelect";
import Commodity from "domain/commodity";
import { DeliveryMethod } from "domain/deliveryMethod";
import DeliveryPeriod, { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import Offer from "domain/offer";
import { TasFallbackOption, getTasFallbackOptionLabel, AllTasFallbackOptions} from "domain/tasFallbackOption";
import PhysicalLocation, { getPhysicalLocation } from "domain/physicalLocation";
import OfferType, { OfferTypeEnum, toContractType } from "domain/offerType";
import React, { useState, useEffect } from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip, Collapse, InputGroup, Alert } from 'react-bootstrap';
import { Trader } from "features/settings/traders/trader";
import NumberEditor from "common/ui/NumberEditor";
import { PaymentOption } from "domain/paymentOption";
import * as NP from "number-precision";
import { AllOrderTypes, AllOrderTypesWithManualEntry, FlatCashOrderTypes, getOrderTypeName, OrderType } from "domain/orderType";
import calculateNumberOfContracts from './contractCalculator';
import FuturesContract from "domain/futuresContract";
import CustomerAccountPicker from "features/settings/producers/CustomerAccountPicker";
import CountdownTimer from "common/ui/CountdownTimer";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { PositionSetting } from "domain/positionSetting";
import { TimerResult } from "react-timer-hook";
import Contract from "domain/contract";
import { formatCustomerWillSell, getPriceLabelForOffer } from "common/formatters/strings";
import { useSelector } from "react-redux";
import IAppStore from "app/IAppStore";
import { getSecondLastBusinessDayOfLastMonth, isCashOfferWithOpenStorage,getRemainingQuantityLabel, getTotalFees, getFinalPrice } from "./helpers";
import { getCommodityPriceIncrement, getAllowedPriceRange } from "features/orders/tasHelpers";
import ContractSubType from "features/contracts/contractSubType";
import contractService from "features/contracts/contractService";
import Merchant, { MerchantFeature, isFeatureEnabled } from "domain/merchant";
import { FeeThin } from "domain/fee";
import AddFeeModal from "./AddFeeModal";
import useLoader from "common/ui/useLoader";
import useAjaxCall from "common/ui/useAjaxCall";
import { logHttpError, withCancellationToken } from "common/http/httpService";
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import offerTypeHelper from "domain/offerTypeHelper";
import unitHelper from "domain/unitHelper";
import classNames from "classnames";
import useTranslators from "common/translation/useTranslators";
import useMarketSchedule from "features/systemConfig/useMarketSchedule";
import { roundCents } from "common/decimal";
import { localizeOfferContract as localize } from "features/localization/localizeService";
import useUnitConversionRates from "features/settings/commodities/useUnitConversionRates";
import { useAllowedPaymentOptions } from "features/merchants/usePaymentOptions";
import LocationPicker from "features/locations/LocationPicker";

export type AddOfferDetailsProps = {
    offer: Offer;
    offerTypes: OfferType[];
    customerWill: boolean[];
    traders: Trader[];
    locations: PhysicalLocation[];
    canChangeCommodity: boolean;
    commodities: Commodity[];
    deliveryMethods: DeliveryMethod[];
    deliveryPeriods: DeliveryPeriod[];
    futuresContracts: FuturesContract[];
    paymentOptions: PaymentOption[];
    delayedPriceContracts: Contract[];
    userIsTrader: boolean;
    isPlaceHedgeOrderChecked: boolean;
    canPlaceHedges: boolean;
    canPushBasis: boolean;
    canManualEntry: boolean;
    isMerchantAutoOfferEnabled: boolean;
    isMerchantAutoPositionEnabled: boolean;
    isPricingDelayedPriceContract: boolean;
    isInitialLocationDetermineLater?: boolean;
    remainingContractQuantity: number;
    isLocationDetermineLater: boolean;
    isMarketOrder: boolean;
    selectedOrderType: OrderType;
    allowPriceChange: boolean;
    isPriceLocked: boolean;
    setPriceLock(value: boolean): void;
    setPlaceHedgeOrderChecked(value: boolean): void;
    onOfferUpdated(offer: Offer): void;
    doesCommodityBalanceExist: boolean;
    maxAllowedQuantity: number;
    positionSettings: PositionSetting[];
    isCashPriceUpdated: boolean;
    showPaymentOption: boolean;
    timer: TimerResult;
    isPricingDelayedPriceContractModal?: boolean;
    realTimeFuturesPrice: number;
    allowPickingCustomer: boolean;
    fees: FeeThin[];
    priceAcrossLocations?: boolean;
    lockDeliveryMethod?: boolean;
    allowCustomerToChangeType?: boolean;
    roundCashPrice?: boolean;
};

const AddOfferDetails: React.FC<AddOfferDetailsProps> = (props: AddOfferDetailsProps) => {
    
    const [showCustomerPanel, setShowCustomerPanel] = useState(!isSetBasisOrSetFuturesOfferType());
    const [userInputPlaceHedgeToggle, setUserInputPlaceHedgeToggle] = useState(null);
    const [contractSubTypes, setContractSubTypes] = useState<ContractSubType[]>([]);
    const [shouldDisplayDeliveryTerms, setShouldDisplayDeliveryTerms] = useState(false);
    const [shouldDisplayCustomerContractId, setShouldDisplayCustomerContractId] = useState(false);
    const [showExtendedFeesPanel, setShowExtendedFeesPanel] = useState(false);

    const marketSchedule = useMarketSchedule();

    const loader = useLoader("Add Offer Details", isLoaded);

    const activeMerchant = useSelector((m: IAppStore) => m.userState.activeMerchant);
    const isCustomer = useSelector((m: IAppStore) => m.userState.isCustomer);
    const userConfiguration = useSelector((m: IAppStore) => m.userState.userConfiguration);
    const regions = props.locations.filter(l => l.homeLocation == null);
    const displaySendToErpToggle = isFeatureEnabled(activeMerchant, MerchantFeature.SendToErpToggle);
    const showLocalUOM = props.offer?.commodity?.baseUnit.id !== props.offer?.commodity?.localUnit.id;
    const unitsConversionRates = useUnitConversionRates();
    const availablePaymentOptionsDetails = useAllowedPaymentOptions(props.paymentOptions);
    const isMaxQuantityReached = isCustomer && props.offer?.producer?.maxOfferQuantity && props.offer.quantity > props.offer.producer.maxOfferQuantity;

    useAjaxCall(cxt => {
        
        async function loadContractSubTypes() {

            try {
                const result = await withCancellationToken(contractService, cxt).listContractSubTypes(
                    toContractType(props.offer.type), 
                    props.offer.customerWillSell, 
                    props.offer.deliveryPeriod?.cropYear.id,
                    props.offer.paymentOption,
                    props.offer.deliveryMethod);
    
                setContractSubTypes(result);
            } catch (error) {
                logHttpError("Error listing contract subtypes", error);
            }
        }

        if (props.offer == null) return;

        loadContractSubTypes();
    }, [props.offer?.type, props.offer?.customerWillSell, props.offer?.deliveryPeriod?.cropYear.id, props.offer?.paymentOption]);


    useEffect(() => {
        if(activeMerchant == null || props.offer == null) return;
        setShouldDisplayDeliveryTerms(displayDeliveryTerms(activeMerchant));

        if(props.offer.deliveryTerms != null && ((!activeMerchant.displayDeliveryTermsWhenCustomerWillBuy && !props.offer?.customerWillSell) ||
            (!activeMerchant.displayDeliveryTermsWhenCustomerWillSell && props.offer?.customerWillSell))) 
        {
            props.onOfferUpdated({ ...props.offer, deliveryTerms: null});
        }
    }, [activeMerchant.displayDeliveryTermsWhenCustomerWillBuy, activeMerchant.displayDeliveryTermsWhenCustomerWillSell, 
        props.offer?.type, props.offer?.customerWillSell])

    useEffect(() => {
        if(activeMerchant == null || props.offer == null) return;
        setShouldDisplayCustomerContractId(displayCustomerContractId(activeMerchant));

        if(props.offer.customerContractId != null && ((!activeMerchant.displayCustomerContractIdForBuy && !props.offer?.customerWillSell) ||
            (!activeMerchant.displayCustomerContractIdForSell && props.offer?.customerWillSell))) 
        {
            props.onOfferUpdated({ ...props.offer, customerContractId: null});
        }
    }, [activeMerchant.displayCustomerContractIdForBuy, activeMerchant.displayCustomerContractIdForSell, props.offer?.customerWillSell])

    useEffect(() => {
        if (props.offer == null) return;

        const shouldShowToggle = showPlaceHedgeToggle();

        if (props.offer.orderType === OrderType.ManualEntry) {
            //1. Manual entry offers show toggle on OfferHedgeSettings
            //2. Always place hedge order for TAS offers
            props.setPlaceHedgeOrderChecked(true);
            return;
        }

        if (!shouldShowToggle && props.isPlaceHedgeOrderChecked) {
            props.setPlaceHedgeOrderChecked(false);
        } 

        if (shouldShowToggle) {
            if (userInputPlaceHedgeToggle == null) {//Set defaults if user input is not available        
                //if autoOffer is disabled, offer type is cash then default the toggle to Off
                if (props.isMerchantAutoOfferEnabled === false && offerTypeHelper.isCash(props.offer?.type) && props.offer?.orderType !== OrderType.Tas) {
                    props.setPlaceHedgeOrderChecked(false);
                }
                else {
                    props.setPlaceHedgeOrderChecked(true);
                }
            }
            else { //if user input is availble then set the same irrespective of other props
                props.setPlaceHedgeOrderChecked(userInputPlaceHedgeToggle);
            }
        }
    }, [props.offer?.customerWillSell, props.offer?.orderType, props.offer?.type, props.offer?.deliveryPeriod, props.canPlaceHedges, props.isLocationDetermineLater, props.isMerchantAutoOfferEnabled, props.isMerchantAutoPositionEnabled, props.positionSettings])

    useEffect(() => {
        if (props.offer == null) return;

        let expirationDate: Date;
        if (props.offer.commodity?.isCashOnly) {
            expirationDate = props.offer.deliveryPeriod?.end ?? props.offer.expirationDate;
        }
        else {
            expirationDate = getExpirationDate(props.offer.futuresContract, props.offer.type);
        }

        if(!moment(props.offer.expirationDate).isSame(moment(expirationDate))) {
            props.onOfferUpdated({ ...props.offer, expirationDate: expirationDate })
        }
    }, [props.offer?.type, props.offer?.futuresContract, props.offer?.deliveryMethod, props.offer?.deliveryPeriod])

    useEffect(() => {
        if (props.offer == null ||
            !props.offer?.producer ||
            isSetBasisOrSetFuturesOfferType() ||
            !props.customerWill.includes(props.offer.producer.willSellDefaultValue)
        ) return;

        props.onOfferUpdated({ ...props.offer, customerWillSell: props.offer.producer.willSellDefaultValue});
    }, [props.offer?.producer])

    function isBasisContractOfferType(): boolean {
        return props.offer?.type === OfferTypeEnum.BasisContract;
    }
    
    function isDelayedPriceContractOfferType(): boolean {
        return props.offer?.type === OfferTypeEnum.DelayedPriceContract;
    }
    
    function isTasOrderType(): boolean {
        return props.offer?.orderType === OrderType.Tas;
    }

    function isBasisOrDelayedPriceContractOfferType(): boolean {
        return [OfferTypeEnum.BasisContract, OfferTypeEnum.DelayedPriceContract].includes(props.offer?.type);
    }
    
    function isFixFuturesOfferType(): boolean {
        return props.offer?.type === OfferTypeEnum.FixFuturesOnBasis;
    }
    
    function isSetBasisOfferType(): boolean {
        return props.offer?.type === OfferTypeEnum.FixBasisOnHta;
    }

    function isSetBasisOrSetFuturesOfferType(): boolean {
        return [OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis].includes(props.offer?.type);
    } 

    function isManualEntryOrderType() {
        return props.offer?.orderType === OrderType.ManualEntry
    }

    function isFlatCashCommodity() {
        return props.offer?.commodity?.isCashOnly;
    }

    function onBasisChanged(value: number) {
        const newOffer = { ...props.offer, basis: value, isBasisFixed: true };
        props.onOfferUpdated(newOffer);
    }

    function showPlaceHedgeToggle(): boolean {
        //Note: whatever props referred here should be added in useEffect as well     
        if(!props.canPlaceHedges ||
            isManualEntryOrderType() ||
            isBasisOrDelayedPriceContractOfferType() ||
            isSetBasisOfferType() ||
            isFlatCashCommodity()
        ) {
            return false;
        }

        const positionSetting = props.positionSettings.find(x => x.commodity.id === props.offer.commodity?.id && 
            x.cropYear.id === props.offer.deliveryPeriod?.cropYear?.id);

        // Set Futures and Market Order and AO is off and AP is on and side is same as Auto order side
        if(isFixFuturesOfferType() && !props.isMerchantAutoOfferEnabled && props.isMerchantAutoPositionEnabled && 
            props.offer.orderType === OrderType.Market && 
            props.offer.customerWillSell === positionSetting?.autoOrderWillSell)
            return false;

        // AO is off but AP is on
        if(!props.isMerchantAutoOfferEnabled && props.isMerchantAutoPositionEnabled)
            return true;

        // HTA Offer - No Location or (AO and AP are both off)
        if (offerTypeHelper.isHta(props.offer?.type) && (props.isLocationDetermineLater || (!props.isMerchantAutoOfferEnabled && !props.isMerchantAutoPositionEnabled)))
            return true;

        // Cash Offer - No Location or AO is off
        if (offerTypeHelper.isCash(props.offer?.type) && !props.isMerchantAutoOfferEnabled)
            return true;
        
        // Set Futures - AO is off
        if (isFixFuturesOfferType() && !props.isMerchantAutoOfferEnabled)
            return true;

        // AO is on and side is opposite of Auto order side
        // Do check for positionSetting null when not comparing for equality
        if(positionSetting != null && props.isMerchantAutoPositionEnabled && props.offer.customerWillSell !== positionSetting.autoOrderWillSell)
            return true;

        return false;
    }

    function getExpirationDate(contract: FuturesContract, type: OfferTypeEnum): Date {
        if(isCashOfferWithOpenStorage(props.offer)) {
            return props.offer?.deliveryPeriod?.end;
        }
        
        if(type === OfferTypeEnum.BasisContract) {
            return getSecondLastBusinessDayOfLastMonth(contract?.expiration);
        }

        const offerExpirationDate = moment(props.offer?.expirationDate);
        const contractExpirationDate = moment(contract?.expiration || null).subtract(1, 'days');
        if (contractExpirationDate.isValid()) {
            return contractExpirationDate.toDate();
        }
        else if (offerExpirationDate.isValid()) {
            return offerExpirationDate.toDate();
        }
        return null;
    }

    function showPriceLockToggle(): boolean {
        if(isCustomer) return false;
        const { trader, type, orderType, futuresContract, commodity } = props.offer;
        return trader?.priceLock && 
            offerTypeHelper.isPriceLockAllowed(type) && 
            orderType === OrderType.Market && 
            futuresContract != null && 
            marketSchedule.isMarketOpen(commodity);
    }

    function disablePriceLockToggle() {
        return !(props.offer.deliveryPeriod && props.offer.futuresContract);
    }

    function showFixBasisToggle() {
        if(isCustomer) 
            return false;
        if (isFixFuturesOfferType())
            return false;
        if (isManualEntryOrderType())
            return false;
        if (!(props.offer?.deliveryMethod === DeliveryMethod.PickedUp || props.canPushBasis))
            return false;
        return true;
    }

    function getMaxQuantity() {
        if (isFixFuturesOfferType() || isSetBasisOfferType())
            return props.remainingContractQuantity;
        else if (showDelayedPriceContract() && props.offer.delayedContract != null)
            return props.offer.delayedContract.remainingQuantity;
        else if (props.isPricingDelayedPriceContractModal)
            return props.remainingContractQuantity;
        else if (props.doesCommodityBalanceExist)
            return props.maxAllowedQuantity;
        else 
            return null;
    }

    function getOrderTypes(forOffer: Offer): OrderType[] {
        if (forOffer?.commodity?.isCashOnly) {
            return FlatCashOrderTypes;
        }

        let orderTypes = null;
        if((offerTypeHelper.isCash(props.offer?.type) || isFixFuturesOfferType() || offerTypeHelper.isHta(props.offer?.type)) && props.canManualEntry)
            orderTypes = AllOrderTypesWithManualEntry;
        else
            orderTypes = AllOrderTypes;

        if (!forOffer?.commodity?.isTasAllowed) {
            orderTypes = orderTypes.filter(ot => ot !== OrderType.Tas);
        }

        return orderTypes;
    }

    function disableBasisField() {
        if(isCustomer) return true;
        if (isManualEntryOrderType())
            return false;
        if (isFixFuturesOfferType())
            return true;
        if ((props.canPushBasis || props.offer.deliveryMethod === DeliveryMethod.PickedUp) && props.offer.isBasisFixed)
            return false;
        return true;
    }

    function shouldShowFreightFee() {
        if(!props.userIsTrader)
            return false;
        
        return props.offer.customerWillSell ? 
            props.offer.deliveryMethod === DeliveryMethod.PickedUp : 
            props.offer.deliveryMethod === DeliveryMethod.Delivered;
    }

    function showDelayedPriceContract() {
        return props.offer.deliveryMethod === DeliveryMethod.DelayedPrice 
            && !props.isPricingDelayedPriceContractModal
            && offerTypeHelper.canHaveDelayedPriceContract(props.offer.type)
            && props.delayedPriceContracts?.length > 0;
    }

    function showRemainingQuantity() {
        return props.doesCommodityBalanceExist 
        || isSetBasisOrSetFuturesOfferType() 
        || props.isPricingDelayedPriceContractModal 
        || (props.offer.deliveryMethod === DeliveryMethod.DelayedPrice && !isDelayedPriceContractOfferType())
        || props.priceAcrossLocations;
    }

    function shouldDisplayRegion() {
        return activeMerchant.displayRegionOnAddOffer && !isCustomer && 
            props.offer?.location?.homeLocation == null && 
            (props.offer?.location == null || 
                !props.locations.some(l => l.homeLocation?.id === props.offer?.location?.id)) &&
            regions?.length > 1;
    }

    function displayDeliveryTerms(merchant: Merchant) {
        if(isCustomer)
            return false;
        if(isSetBasisOrSetFuturesOfferType())
            return false;
        if(isDelayedPriceContractOfferType())
            return false;
        if(!merchant.displayDeliveryTermsWhenCustomerWillBuy && !props.offer?.customerWillSell)
            return false;
        if(!merchant.displayDeliveryTermsWhenCustomerWillSell && props.offer?.customerWillSell)
            return false;
        return true;
    }

    function displayCustomerContractId(merchant: Merchant) {
        if(isCustomer)
            return false;
        if(!merchant.displayCustomerContractIdForBuy && !props.offer?.customerWillSell)
            return false;
        if(!merchant.displayCustomerContractIdForSell && props.offer?.customerWillSell)
            return false;
        return true;
    }

    function getRegion(l: PhysicalLocation) {
        if(activeMerchant.displayRegionOnAddOffer) {
            if (l == null) {
                return regions?.length > 0 ? regions[0] : null;
            }
            else {
                return l.homeLocation != null ? getPhysicalLocation(l.homeLocation) : l;
            }
        } 
        return null;
    }

    function showPriceAtOpen(priceLock?: boolean, ordertype?: OrderType) {
        return !(priceLock ?? props.isPriceLocked) && 
            (ordertype ?? props.offer.orderType) === OrderType.Market && 
            !marketSchedule.isMarketOpen(props.offer?.commodity);
    }

    function isLoaded(): boolean {
        return props.offer != null;
    }

    function getQuantityInLocal(value: number) {
        return unitHelper.getLocalQuantity(value, props.offer.commodity, unitsConversionRates);
    }

    function getPriceInLocal(value: number) {
        let localPrice = unitHelper.getLocalPrice(value, props.offer.commodity, unitsConversionRates);
        if(props.roundCashPrice){
            localPrice = roundCents(localPrice, (props.offer.futuresContract?.isSettled ? 
                props.offer.deliveryPeriod.commodity.roundingOnClose : 
                props.offer.deliveryPeriod.commodity.roundingInSession))
        }
        return localPrice;
    }   

    function getPriceText(price: number) {
        return unitHelper.getPriceText(price, showLocalUOM, props.offer.currency.code, props.offer.commodity, unitsConversionRates);
    }

    function getFeesText(){
        const fees = getTotalFees(props.offer);
        return getPriceText(fees);
    }

    if (!loader.checkIsLoaded()) return <></>;

    const priceRange = getAllowedPriceRange(props.offer.orderType === OrderType.Tas, false, props.offer.commodity);
    const isCustomerInSetFuturesOrSetBasisMode = isCustomer && isSetBasisOrSetFuturesOfferType();

    return <>
            {isSetBasisOrSetFuturesOfferType() && <Form.Group as={Row}>
                <Form.Label column>{isSetBasisOfferType() ? "HTA Contract ID" : "Basis Contract ID"}</Form.Label>
                <Col xs="8">
                    <div id="basisContractId" className="col-form-label">#{props.offer.displayId}</div>
                </Col>
            </Form.Group>}
            {!isSetBasisOrSetFuturesOfferType() &&
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_Type")}</Form.Label>
                    <Col xs="8">
                        <TypedSelect
                            options={props.offerTypes}
                            value={{id: props.offer.type}}
                            valueGetter={m => m.id}
                            textGetter={m => m.name}
                            className={props.canPushBasis ? "font-weight-bold" : ""}
                            disabled={isCustomer && !props.allowCustomerToChangeType}
                            enforceValidOption
                            onValueSelected={m => {
                                const orderType = offerTypeHelper.canBeHedged(m.id) 
                                    ? props.offer.orderType ?? props.selectedOrderType
                                    : null;
                                
                                const defaultDeliveryMethod = !props.lockDeliveryMethod && m.id === OfferTypeEnum.DelayedPriceContract 
                                    ? DeliveryMethod.DelayedPrice 
                                    : props.offer.deliveryMethod;
                                props.onOfferUpdated({ ...props.offer, type: m.id, orderType: orderType, deliveryMethod: defaultDeliveryMethod }) 
                            }} />
                    </Col>
                </Form.Group>
            }

            <Form.Group as={Row} className={!showCustomerPanel && "mb-0"}>
                {isSetBasisOrSetFuturesOfferType() && <Form.Label column="sm" sm={1} className="text-left">
                    <span onClick={() => setShowCustomerPanel(!showCustomerPanel)} aria-controls="customer-collapse" aria-expanded={showCustomerPanel}>
                        <i className="material-icons">{showCustomerPanel ? 'expand_less' : 'expand_more'}</i>
                    </span>
                </Form.Label>}
                {!isCustomer && props.allowPickingCustomer && <>
                    <Form.Label column>{localize("Details_Label_AccountCustomer")}</Form.Label>
                    <Col xs="8">
                        {(isSetBasisOrSetFuturesOfferType() || props.isPricingDelayedPriceContractModal) ?
                            <div className="col-form-label">{getProducerDisplayName(props.offer.producer)}</div>
                            : <CustomerAccountPicker
                                showClearButton
                                inputProps={{ required: true, 'aria-label': "Customer" }}
                                selected={props.offer.producer}
                                selectedAccount={props.offer.customerAccount?.accountId}
                                onCustomerSelected={ (c, a) => props.onOfferUpdated({...props.offer, producer: c, customerAccount: a})}
                        />}
                    </Col>
                </>}
                {!isCustomerInSetFuturesOrSetBasisMode && (isCustomer || !props.allowPickingCustomer) && <>
                    <Form.Label column>{localize("Details_Label_Customer")}</Form.Label>
                    <Col xs="8">
                        <div className="col-form-label">{getProducerDisplayName(props.offer.producer)}</div>
                    </Col>                    
                    </>
                }
                {isCustomerInSetFuturesOrSetBasisMode && <AccountIdSelect {...props} />}
            </Form.Group>  
            {!isCustomerInSetFuturesOrSetBasisMode && (isCustomer || (!props.allowPickingCustomer && props.offer.producer?.accounts != null)) && <>
                <Form.Group as={Row}>
                    <AccountIdSelect {...props} />
                </Form.Group>
                </>
            } 

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_CustomerWill")}</Form.Label>
                <Col xs="8">
                    {isSetBasisOrSetFuturesOfferType() ?
                        <div className="col-form-label">{formatCustomerWillSell(props.offer.customerWillSell)}</div>
                        : <TypedSelect
                        options={props.customerWill}
                        value={props.offer.customerWillSell}
                        disabled={!(userConfiguration.canBuy && userConfiguration.canSell)}
                        valueGetter={m => m}
                        textGetter={m => formatCustomerWillSell(m)}
                        onValueSelected={m => {
                            const defaultBuyLocation = props.locations.find(l => l.isDefaultBuyLocation);
                            if(!m && defaultBuyLocation != null) {
                                props.onOfferUpdated({... props.offer, customerWillSell: m, 
                                    location: defaultBuyLocation, contractLocation: defaultBuyLocation, region: getRegion(defaultBuyLocation) })
                            }
                            else {
                                props.onOfferUpdated({ ...props.offer, customerWillSell: m})} 
                            }
                        }/>}
                </Col>
            </Form.Group>

            <Collapse in={showCustomerPanel} className={isSetBasisOrSetFuturesOfferType() && "border-top border-bottom pt-2 pb-1 mb-2"}>
                <div id="customer-collapse">

                    <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_Commodity")}</Form.Label>
                        <Col xs="8">
                            {isSetBasisOrSetFuturesOfferType() ?
                                <div className="col-form-label">{props.offer.commodity.name}</div>
                                : <TypedSelect
                                    disabled={!props.canChangeCommodity || isCustomer}
                                    value={props.offer.commodity}
                                    options={props.commodities}
                                    valueGetter={c => c.id}
                                    textGetter={c => c.name}
                                    onValueSelected={c => props.onOfferUpdated({ ...props.offer, commodity: c })} />}
                        </Col>
                    </Form.Group>

                    {activeMerchant.displayContractLocation && !isCustomer && <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_ContractLocation")}</Form.Label>
                        <Col xs="8">
                            {(isSetBasisOrSetFuturesOfferType() && !props.isInitialLocationDetermineLater) ?
                                <div className="col-form-label">{props.offer.contractLocation?.name}</div>
                                : <LocationPicker
                                    disabled={!props.canPushBasis && props.isPriceLocked && offerTypeHelper.isCash(props.offer?.type)}
                                    required={!props.canPlaceHedges || !activeMerchant.isDetermineLaterAllowed || props.offer.commodity?.isCashOnly}
                                    locations={props.locations}
                                    placeholder={props.canPlaceHedges && activeMerchant.isDetermineLaterAllowed && !props.offer.commodity?.isCashOnly ? "Determine Later" : "Select"}
                                    selected={props.offer.contractLocation}
                                    onLocationSelected={l => props.onOfferUpdated({ ...props.offer, contractLocation: l })}
                                />}
                        </Col>
                    </Form.Group>}

                    <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_Location")}</Form.Label>
                        <Col xs="8">
                            {(isSetBasisOrSetFuturesOfferType() && !props.isInitialLocationDetermineLater) ?
                                <div className="col-form-label">{props.offer.location?.name}</div>
                                : <LocationPicker
                                    disabled={props.priceAcrossLocations || (!props.canPushBasis && props.isPriceLocked && offerTypeHelper.isCash(props.offer?.type))}
                                    required={!props.canPlaceHedges || !activeMerchant.isDetermineLaterAllowed || props.offer.commodity?.isCashOnly}
                                    locations={props.locations}
                                    placeholder={props.canPlaceHedges && activeMerchant.isDetermineLaterAllowed && !props.offer.commodity?.isCashOnly ? "Determine Later" : "Select"}
                                    selected={props.offer.location}
                                    onLocationSelected={l => props.onOfferUpdated({ ...props.offer, location: l, region: getRegion(l) })}
                                />}
                        </Col>
                    </Form.Group>

                    {shouldDisplayRegion() && <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_Region")}</Form.Label>
                        <Col xs="8">
                            <LocationPicker
                                required
                                locations={regions}
                                selected={props.offer.region}
                                placeholder={"Select"}
                                onLocationSelected={l => { props.onOfferUpdated({ ...props.offer, region: l }) }}
                            />
                        </Col>
                    </Form.Group>}

                    {showDelayedPriceContract() && <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_DeliveryPeriodContract")}</Form.Label>
                        <Col xs="8">
                            <TypedSelect
                                value={props.offer.delayedContract}
                                options={props.delayedPriceContracts}
                                showNullRow={props.delayedPriceContracts?.length === 0}
                                nullRowText="Select"
                                required
                                valueGetter={m => m.internalId}
                                textGetter={m => m.displayId}
                                onValueSelected={dc => props.onOfferUpdated({ ...props.offer, delayedContract: dc, sourceContractId: dc?.internalId, quantity: dc?.remainingQuantity })} />
                        </Col>
                    </Form.Group>}

                    {!isSetBasisOrSetFuturesOfferType() && (!isDelayedPriceContractOfferType() || activeMerchant.hasErpIntegration) && <DeliveryMethodComponent {...props} />}


                    {shouldShowFreightFee() && <Form.Group as={Row}>
                        <Form.Label column className="optional">{localize("Details_Label_FreightFee")}</Form.Label>
                        <Col xs="8">
                            <NumberEditor className='form-control'
                                min={0}
                                step={.01}
                                allowDecimals
                                precision={4}
                                enforcePrecision
                                value={props.offer.freightFee}
                                onValueChange={value => { props.onOfferUpdated({ ...props.offer, freightFee: value }); }}
                            />
                        </Col>
                    </Form.Group>}

                    {!isDelayedPriceContractOfferType() && <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_DeliveryPeriod")}</Form.Label>
                        <Col xs="8">
                            {isSetBasisOrSetFuturesOfferType() ?
                                <div className="col-form-label">{props.offer.deliveryPeriod?.id == null ? "Select" : 
                                        getDeliveryPeriodDisplayName(props.offer.deliveryPeriod)}</div>
                                : <TypedSelect
                                    value={props.offer.deliveryPeriod}
                                    options={props.deliveryPeriods}
                                    showNullRow
                                    nullRowText="Select"
                                    required
                                    valueGetter={dp => dp.id}
                                    textGetter={dp => getDeliveryPeriodDisplayName(dp)}
                                    disabled={!props.canPushBasis && props.isPriceLocked && offerTypeHelper.isCash(props.offer?.type)}
                                    onValueSelected={dp => 
                                        props.onOfferUpdated({ ...props.offer, 
                                            deliveryPeriod: dp, 
                                            futuresContract: dp?.contract,
                                            deliveryPeriodStartDate: dp?.start, 
                                            deliveryPeriodEndDate: dp?.end, 
                                            htaFee: offerTypeHelper.isHta(props.offer?.type) ? dp?.htaFee : props.offer.htaFee,
                                            isPriceAtOpenEnabled: showPriceAtOpen() ? activeMerchant.isPriceAtOpenEnabled : false
                                        })
                                    } 
                                    />}
                        </Col>
                    </Form.Group>
                    }

                    {offerTypeHelper.isHta(props.offer?.type) && <Form.Group as={Row}>
                        <Form.Label column>{localize("Details_Label_HtaFee")}</Form.Label>
                        <Col xs="8">  
                            <NumberEditor className="form-control"
                                allowDecimals
                                enforcePrecision
                                precision={4}
                                disabled={isCustomer || !props.canPushBasis}
                                value={props.offer.htaFee}
                                onValueChange={value => props.onOfferUpdated({...props.offer, htaFee: value })}
                            />                         
                        </Col>
                    </Form.Group>}
                </div>
            </Collapse>

            {isSetBasisOrSetFuturesOfferType() && <DeliveryMethodComponent {...props} />}

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_StartDate")}</Form.Label>
                <Col xs="8">
                    {!isCustomer ?
                        <Form.Control type="date"
                            value={formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}
                            required
                            min={formatUTCDate(props.offer.deliveryPeriod?.start, "yyyy-MM-DD")}
                            max={formatUTCDate(props.offer.deliveryPeriod?.end, "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onOfferUpdated({ ...props.offer, deliveryPeriodStartDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                        </Form.Control> :
                        <div className="col-form-label">{formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}</div>
                    }
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_EndDate")}</Form.Label>
                <Col xs="8">
                    {!isCustomer ? 
                        <Form.Control type="date"
                            value={formatUTCDate(props.offer.deliveryPeriodEndDate, "yyyy-MM-DD")}
                            required
                            min={formatUTCDate(props.offer.deliveryPeriodStartDate, "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onOfferUpdated({ ...props.offer, deliveryPeriodEndDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate() })}>
                        </Form.Control> :
                        <div className="col-form-label">{formatUTCDate(props.offer.deliveryPeriodEndDate, "yyyy-MM-DD")}</div>
                    }
                </Col>
            </Form.Group>
                                   
            <Form.Group as={Row} className={!showExtendedFeesPanel && props.offer.fees.length > 0 && "mb-0"}>
                {props.fees.length > 0 && <Form.Label column className="text-left">
                    <span onClick={() => setShowExtendedFeesPanel(!showExtendedFeesPanel)} aria-controls="fees-collapse" aria-expanded={showExtendedFeesPanel}>
                        <i className="material-icons">{showExtendedFeesPanel ? 'expand_less' : 'expand_more'}</i>
                    </span>
                </Form.Label>}
                <Form.Label column className="pl-0">{localize("Details_Label_TotalFees")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getFeesText()}</div> 
                </Col>                    
            </Form.Group> 

            <Collapse in={showExtendedFeesPanel} className={"border-top border-bottom pt-2 pb-1 mb-2"}>
                <div id="fees-collapse">
                    <AddFeeModal fees={props.fees}
                        selectedFees={props.offer?.fees}
                        disabled={isCustomer}
                        onFeesChanged={(fees) => props.onOfferUpdated({...props.offer, fees: fees})}
                    />
                </div>
            </Collapse>

            {!isDelayedPriceContractOfferType() && !isFlatCashCommodity() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Symbol")}</Form.Label>
                <Col xs="8">
                    {isSetBasisOrSetFuturesOfferType() ?
                        <div className="col-form-label">{props.offer.futuresContract.symbol}</div>
                        : <TypedSelect
                            disabled={isCustomer || (offerTypeHelper.isCash(props.offer?.type) && ((!props.canPushBasis && props.isPriceLocked) || !props.offer.isBasisFixed))}
                            showNullRow={props.canPushBasis}
                            nullRowText="Select"
                            required
                            value={props.offer.futuresContract}
                            options={props.futuresContracts}
                            valueGetter={fc => fc.id}
                            textGetter={fc => fc.symbol}
                            onValueSelected={fc => props.onOfferUpdated({ ...props.offer, futuresContract: fc })}
                        />}
                </Col>
            </Form.Group>}

            {(!(offerTypeHelper.isHta(props.offer?.type) || isDelayedPriceContractOfferType())) && !isFlatCashCommodity() && <Form.Group as={Row} className="align-items-end">
                <Form.Label column>{localize("Details_Label_Basis")}</Form.Label>
                <Col xs="4">
                    <NumberEditor className="form-control"
                        disabled={disableBasisField()}
                        required={props.offer.isBasisFixed}
                        step={.01}
                        allowDecimals
                        precision={5}
                        enforcePrecision
                        value={props.offer.basis ?? ''}
                        onValueChange={value => onBasisChanged(value)}
                        placeholder={'--'}
                    />
                </Col>
                <Col xs="4" className="p-0">
                    {showFixBasisToggle() && <>
                        <Form.Label>{localize("Details_Label_SetBasis")}</Form.Label>&nbsp;
                        <Form.Check type="switch" id="fix-basis" label="" className="d-inline-block" checked={props.offer.isBasisFixed} 
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                props.onOfferUpdated({ ...props.offer, isBasisFixed: ev.target.checked })
                            }}/>
                    </>}
                </Col>
            </Form.Group>}
            
            {!(isBasisOrDelayedPriceContractOfferType() || isSetBasisOfferType()) && !isCustomer && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_OrderType")}</Form.Label>
                <Col xs="4">
                    <TypedSelect
                        options={getOrderTypes(props.offer)}
                        value={props.offer.orderType}
                        valueGetter={o => o}
                        textGetter={o => getOrderTypeName(o)}
                        className="font-weight-bold pl-2"
                        enforceValidOption
                        onValueSelected={o => {
                            const isMarket = o === OrderType.Market;
                            let priceLock = props.isPriceLocked;
                            if (props.isPriceLocked && !isMarket) priceLock = false;
                            if (props.offer.trader?.priceLock != null && 
                                isMarket && 
                                marketSchedule.isMarketOpen(props.offer?.commodity)) {
                                priceLock = true;
                            }
                            props.setPriceLock(priceLock);
                            props.onOfferUpdated({ ...props.offer, orderType: o,
                                interestStartDate: props.offer.paymentOption === PaymentOption.DeferredPay ? (o !== OrderType.Limit ? formatTodaysDate('yyyy-MM-DD') : null) : null,
                                setInterestStartDateOnDateOfFill: props.offer.paymentOption === PaymentOption.DeferredPay && o === OrderType.Limit ? true : null,
                                isPriceAtOpenEnabled: showPriceAtOpen(priceLock, o) ? activeMerchant.isPriceAtOpenEnabled : false,
                                tasFallbackOption: o === OrderType.Tas && props.offer?.tasFallbackOption === undefined? TasFallbackOption.NotifyOnly : props.offer?.tasFallbackOption});
                        }} />
                </Col>
                <Col xs="4" className={classNames({'disabled-control': disablePriceLockToggle()}, "p-0")}>
                    {showPriceLockToggle() && <>
                        <OverlayTrigger overlay={
                            disablePriceLockToggle() ? <Tooltip id={`tooltip-price-lock`}>
                                {localize("Details_Tooltip_PriceLock")}
                            </Tooltip> : <span></span>
                        }>
                            <span>
                                <Form.Label>{localize("Details_Label_PriceLock")}</Form.Label> &nbsp;
                                <Form.Check type="switch" id="price-lock" label="" className="d-inline-block" checked={props.isPriceLocked}
                                    disabled={disablePriceLockToggle()}
                                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.setPriceLock(ev.target.checked)}
                                />
                            </span>
                        </OverlayTrigger>
                        
                        {props.isPriceLocked && <CountdownTimer timer={props.timer} />}
                    </>}
                    {showPriceAtOpen() && <>
                        <Form.Label>{localize("Details_Label_PriceAtOpen")}</Form.Label>&nbsp;
                        <Form.Check type="switch" id="price-at-open" label="" className="d-inline-block" checked={props.offer.isPriceAtOpenEnabled} 
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                props.onOfferUpdated({ ...props.offer, isPriceAtOpenEnabled: ev.target.checked })
                            }}/>
                    </>}
                </Col>
            </Form.Group>}

            {(!isTasOrderType() && !isFlatCashCommodity() && (isSetBasisOrSetFuturesOfferType() || isManualEntryOrderType() || 
                (props.offer.orderType === OrderType.Limit && (offerTypeHelper.isCash(props.offer?.type) || offerTypeHelper.isHta(props.offer?.type))))) && 
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_CurrentFutures")}</Form.Label>
                    <Col xs="8">
                        <div className="col-form-label">
                            {formatNumber(props.realTimeFuturesPrice, "0,0.0000")}
                        </div>
                    </Col>
                </Form.Group>
            }
            {!isTasOrderType() && !isFlatCashCommodity() && (isSetBasisOrSetFuturesOfferType() || isManualEntryOrderType() || 
                (offerTypeHelper.isCash(props.offer.type) && props.offer.orderType === OrderType.Limit)) &&
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_RequiredFutures")}</Form.Label>
                    <Col xs="4">
                        {(isSetBasisOfferType() || (offerTypeHelper.isCash(props.offer?.type) && props.offer.orderType === OrderType.Limit)) ?
                            <div className="col-form-label">{formatNumber(props.offer.futuresPrice, "0,0.0000")}</div>
                            : <NumberEditor className={props.isCashPriceUpdated ? 'form-control font-weight-bold' : 'form-control'}
                            disabled={props.allowPriceChange ? false : props.isMarketOrder}
                            required={!props.isMarketOrder}
                            min={0}
                            step={getCommodityPriceIncrement(props.offer.deliveryPeriod?.commodity, props.offer.orderType) ?? .01}
                            allowDecimals
                            precision={5}
                            enforcePrecision
                            roundingCent
                            value={props.offer.futuresPrice}
                            onValueChange={value => { props.onOfferUpdated({ ...props.offer, futuresPrice: value }); }}
                        />}
                    </Col>
                    <Col xs="4">
                    </Col>
                </Form.Group>
            }

            {!isBasisOrDelayedPriceContractOfferType() && <Form.Group as={Row}>
                <Form.Label column>{getPriceLabelForOffer(props.offer.type, props.offer.orderType)}</Form.Label>
                <Col xs="4">
                    <CashPrice value={props.offer.cashPrice}
                        isPricingType={isSetBasisOrSetFuturesOfferType() && !isTasOrderType()}
                        isManualEntry={isManualEntryOrderType()}
                        allowPriceChange={props.allowPriceChange}
                        isCashPriceUpdated={props.isCashPriceUpdated}
                        isMarketOrder={props.isMarketOrder}
                        priceIncrement={getCommodityPriceIncrement(props.offer.deliveryPeriod?.commodity, props.offer.orderType) ?? .01}
                        min={priceRange.min ?? 0}
                        max={priceRange.max}
                        isPriceLocked={props.isPriceLocked}
                        onValueChanged={value => { 
                            const futuresPrice = NP.minus(value ?? 0, props.offer.basis ?? 0);
                            props.onOfferUpdated({ ...props.offer, cashPrice: value, futuresPrice }); 
                        }} 
                        currencyCode={props.offer.currency.code}
                    />
                </Col>
                <Col xs="4" className="pl-0">
                {showLocalUOM && !isTasOrderType() && <CashPrice value={getPriceInLocal(props.offer.cashPrice)}
                        isPricingType={isSetBasisOrSetFuturesOfferType() && !isTasOrderType()}
                        isManualEntry={isManualEntryOrderType()}
                        allowPriceChange={props.allowPriceChange}
                        isCashPriceUpdated={props.isCashPriceUpdated}
                        isMarketOrder={props.isMarketOrder}
                        min={priceRange.min ?? 0}
                        max={priceRange.max}
                        isPriceLocked={props.isPriceLocked}
                        onValueChanged={value => { 
                            const basePrice = unitHelper.getBasePrice(value, props.offer.commodity, unitsConversionRates);
                            const futuresPrice = NP.minus(basePrice ?? 0, props.offer.basis ?? 0);
                            props.onOfferUpdated({ ...props.offer, cashPrice: basePrice, futuresPrice }); 
                        }} 
                        currencyCode={props.offer.currency.code}
                    />}</Col>
            </Form.Group>}
            {!(isBasisOrDelayedPriceContractOfferType() || isSetBasisOfferType()) && isCustomer && <Form.Group as={Row}>
                <Form.Label column></Form.Label>
                <Col xs="8">
                    <Form.Label>{localize("Details_Label_AtMarket")}</Form.Label>&nbsp;
                    <Form.Check type="switch" id="at-market" label="" className="d-inline-block"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.onOfferUpdated({...props.offer, orderType: event.target.checked ? OrderType.Market : OrderType.Limit})} />
                </Col>
            </Form.Group>}

            {props.offer.orderType === OrderType.Tas && (
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_TasDoesNotFill")}</Form.Label>
                    <Col xs="8">
                        <TypedSelect
                            value={props.offer.tasFallbackOption}
                            options={AllTasFallbackOptions}
                            valueGetter={o => o}
                            textGetter={o => getTasFallbackOptionLabel(o)}
                            enforceValidOption
                            onValueSelected={value => {
                                props.onOfferUpdated({ ...props.offer, tasFallbackOption: value as TasFallbackOption });
                            }}
                        />
                    </Col>
                </Form.Group>
            )}

            {props.offer.deliveryPeriod?.premium != null && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Premium")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getPriceText(props.offer.deliveryPeriod?.premium)}</div>
                </Col>
            </Form.Group>}

            {!isBasisOrDelayedPriceContractOfferType() && !isTasOrderType() && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_FinalPrice")}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{getPriceText(getFinalPrice(props.offer))}</div>
                </Col>
            </Form.Group>}

            <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Quantity")}</Form.Label>
                <Col xs={showLocalUOM ? "4" : "8"}>
                    <NumberEditor
                        className={classNames('form-control', {"quantity-alert max-quantity-reached": isMaxQuantityReached})}
                        aria-label="Quantity"
                        required
                        allowDecimals
                        precision={2}
                        thousandSeparator
                        unit={props.offer.commodity.baseUnit.abbreviation}
                        min={0}
                        disabled={props.doesCommodityBalanceExist && props.maxAllowedQuantity === 0}
                        isInvalidMessage="Offer quantity exceeds maximum"
                        max={getMaxQuantity()}
                        value={props.offer.quantity}
                        onValueChange={value => props.onOfferUpdated({...props.offer, quantity:value, 
                            numberOfContracts: calculateNumberOfContracts(props.offer.commodity, value, props.offer.useMini)})}
                        validationName="quantity"
                        type="text"
                    />
                </Col>
                {showLocalUOM && <Col xs="4" className="pl-0">
                    <NumberEditor 
                        className={classNames('form-control', {"quantity-alert max-quantity-reached": isMaxQuantityReached})}
                        aria-label="Quantity-Local"
                        required
                        allowDecimals
                        precision={3}
                        thousandSeparator
                        unit={props.offer.commodity.localUnit.abbreviation}
                        min={0}
                        disabled={props.doesCommodityBalanceExist && props.maxAllowedQuantity === 0}
                        isInvalidMessage="Offer quantity exceeds maximum"
                        max={getQuantityInLocal(getMaxQuantity())}
                        value={getQuantityInLocal(props.offer.quantity)}
                        onValueChange={value => {
                            const quantityInBase = unitHelper.getBaseQuantity(value, props.offer.commodity, unitsConversionRates);
                            props.onOfferUpdated({...props.offer, quantity: quantityInBase, 
                            numberOfContracts: calculateNumberOfContracts(props.offer.commodity, quantityInBase, props.offer.useMini)})}
                        }
                        validationName="quantity"
                    />
                </Col>}
            </Form.Group>

            {showRemainingQuantity() && <Form.Group as={Row}>
                <Form.Label column> {getRemainingQuantityLabel(props.offer?.type, props.offer?.deliveryMethod)}</Form.Label>
                <Col xs="8">
                    <div className="col-form-label">{props.doesCommodityBalanceExist ?
                        formatNumber(NP.minus(props.maxAllowedQuantity ?? 0, props.offer.quantity ?? 0), '0,0.00') :
                        formatNumber(NP.minus(props.remainingContractQuantity ?? 0, props.offer.quantity ?? 0), '0,0.00')}
                    </div>
                    
                </Col>
            </Form.Group>}

            {isCustomer && <Alert variant='primary' className="d-flex align-items-center quantity-alert">
                <i className="material-icons info-icon mr-2 quantity-alert">info</i>
                {
                    !props.offer?.producer?.maxOfferQuantity ?
                        <span>Offers require approval. Once submitted, they can be found in Pending Offers.</span>
                        :
                        <span>Offers over <strong>{formatNumber(props.offer.producer.maxOfferQuantity, '0,0')}</strong> require approval and once submitted, they can be found in Pending Offers. </span>
                }
            </Alert>}

            {!isTasOrderType() && (props.offer.orderType !== OrderType.Market || isBasisContractOfferType() || isDelayedPriceContractOfferType()) &&
            <Form.Group as={Row}>
                <Form.Label column>{(isBasisContractOfferType() || isDelayedPriceContractOfferType()) ? "Final Pricing Date" : "Expiration"}</Form.Label>
                <Col xs="8">
                    <Form.Control type="date"
                        value={formatUTCDate(props.offer.expirationDate, "yyyy-MM-DD")}
                        required
                        max={formatDate(isCashOfferWithOpenStorage(props.offer) ? toUtcDay(props.offer.deliveryPeriod?.end) : getPreviousDayForGivenDate(toUtcDay(props.offer.futuresContract?.expiration)), "yyyy-MM-DD")}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onOfferUpdated({ ...props.offer, expirationDate: moment.utc(moment(ev.target.value).format("YYYY-MM-DD")).toDate()})}>
                    </Form.Control>
                </Col>
            </Form.Group>
            }

            {!isCustomer && <Form.Group as={Row}>
                <Form.Label column>{localize("Details_Label_Trader")}</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        value={props.offer.trader}
                        required
                        options={props.traders}
                        valueGetter={m => m.userId}
                        textGetter={m => `${m.firstName} ${m.lastName}`}
                        onValueSelected={m => props.onOfferUpdated({ ...props.offer, trader: m})} />
                </Col>
            </Form.Group>}
            
            {props.showPaymentOption &&
                <Form.Group as={Row}>
                    <Form.Label column>{localize("Details_Label_PaymentOption")}</Form.Label>
                    <Col xs="8">
                        <TypedSelect
                            value={props.offer.paymentOption}
                            options={availablePaymentOptionsDetails}
                            valueGetter={p => p?.paymentOption ?? p}
                            textGetter={p => p.displayName}
                            onValueSelected={p => props.onOfferUpdated({ ...props.offer,
                                paymentOption: p.paymentOption,
                                deferredPayDate: p.paymentOption !== PaymentOption.DeferredPay ? null : firstBusinessDayOfNextYear(),
                                interestRate: p.paymentOption === PaymentOption.DeferredPay && activeMerchant.defaultDeferPayInterestRate != null ? activeMerchant.defaultDeferPayInterestRate : null,
                                interestStartDate: p.paymentOption !== PaymentOption.DeferredPay ? null : (props.offer.orderType === OrderType.Limit ? null : formatTodaysDate('yyyy-MM-DD')),
                                setInterestStartDateOnDateOfFill: p.paymentOption === PaymentOption.DeferredPay && props.offer.orderType === OrderType.Limit ? true : null })} />
                    </Col>
                </Form.Group>
            }
            
            {contractSubTypes.length > 1 && props.offer.deliveryMethod !== DeliveryMethod.DelayedPrice &&
            <Form.Group as={Row}>
                <Form.Label column>Contract Sub-type</Form.Label>
                <Col xs="8">
                    <TypedSelect
                        required
                        showNullRow
                        value={{value: props.offer.contractSubType}}
                        options={contractSubTypes}
                        valueGetter={p => p.value}
                        textGetter={p => p.name}
                        onValueSelected={p => props.onOfferUpdated({ ...props.offer, contractSubType: p.value})} />
                </Col>
            </Form.Group>}

            {props.offer.paymentOption === PaymentOption.DeferredPay && <div>
                <Form.Group as={Row}>
                    <Form.Label column>Pay Date</Form.Label>
                    <Col xs="8">
                        <Form.Control type="date"
                            value={props.offer.deferredPayDate}
                            required
                            min={formatDate(getNextDay(), "yyyy-MM-DD")}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onOfferUpdated({ ...props.offer, deferredPayDate: ev.target.value})}>
                        </Form.Control>
                    </Col>
                </Form.Group>

                <Form.Group as={Row}>
                    <Form.Label column className="optional">Interest Start Date</Form.Label>
                    {props.offer?.orderType == OrderType.Limit && <Col xs="4" className="pt-2">
                        <Form.Check type="switch" id="on-date-of-fill" label="" className="d-inline-block" checked={props.offer.setInterestStartDateOnDateOfFill} 
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                props.onOfferUpdated({ ...props.offer, setInterestStartDateOnDateOfFill: ev.target.checked, 
                                    interestStartDate: ev.target.checked ? null : props.offer?.interestStartDate })
                            }}/>&nbsp;
                        <Form.Label className="optional">On Date of Fill</Form.Label>
                    </Col>}
                    <Col xs={props.offer?.orderType == OrderType.Limit ? "4" : "8"}>
                        {!props.offer.setInterestStartDateOnDateOfFill && <Form.Control type="date"
                            value={props.offer.interestStartDate}
                            min={formatTodaysDate('yyyy-MM-DD')}
                            max={props.offer.deferredPayDate}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => ev.target.value && props.onOfferUpdated({ ...props.offer, interestStartDate: ev.target.value})}>
                        </Form.Control>}
                    </Col>
                </Form.Group>

                <Form.Group as={Row}>
                    <Form.Label column className="optional">Interest Rate</Form.Label>
                    <Col xs="4">
                    {activeMerchant.defaultDeferPayInterestRate != null ?
                        <div className="col-form-label">{activeMerchant.defaultDeferPayInterestRate} %</div>
                        : <InputGroup className="rate-input"><NumberEditor className="form-control"
                            aria-label="InterestRate"
                            allowDecimals
                            precision={4}
                            min={0}
                            max={100}
                            placeholder={"0.0000"}
                            value={props.offer.interestRate}
                            onValueChange={value => props.onOfferUpdated({...props.offer, interestRate:value })}
                        />
                        <InputGroup.Append className="rate-text">
                            <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup.Append></InputGroup>
                    }
                    </Col>
                    <Col></Col>
                </Form.Group>
            </div>}
            
            <Form.Group as={Row}>
                <Form.Label column className="optional">Comment</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.offer.comments}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onOfferUpdated({...props.offer, comments: ev.target.value})}>
                    </Form.Control>
                </Col>
            </Form.Group>
            
            {shouldDisplayDeliveryTerms && <Form.Group as={Row}>
                <Form.Label column className="optional">Delivery Terms</Form.Label>
                <Col xs="8">
                    {isSetBasisOrSetFuturesOfferType() ?
                        <div className="col-form-label">{props.offer.deliveryTerms}</div>: 
                        <Form.Control
                            value={props.offer.deliveryTerms}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onOfferUpdated({...props.offer, deliveryTerms: ev.target.value})}>
                        </Form.Control>
                    }
                </Col>
            </Form.Group>}

            {shouldDisplayCustomerContractId && <Form.Group as={Row}>
                <Form.Label column className="optional">Cust. Contract ID</Form.Label>
                <Col xs="8">
                    <Form.Control
                        value={props.offer.customerContractId}
                        required={activeMerchant.makeCustomerContractIdRequired}
                        onChange={(ev: React.ChangeEvent<HTMLInputElement>) => props.onOfferUpdated({...props.offer, customerContractId: ev.target.value})}>
                    </Form.Control>
                </Col>
            </Form.Group>}

            {!isBasisOrDelayedPriceContractOfferType() && !isCustomer && <Form.Group as={Row}>
                <Col xs="12" className="text-right">
                <Form.Label>Send notification to customer</Form.Label>&nbsp;
                <Form.Check type="switch" id="sendNotificationToCustomer" label="" className="d-inline-block" checked={props.offer.notifyCustomer}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => { 
                        props.onOfferUpdated({...props.offer, notifyCustomer: ev.target.checked}); 
                    }}/>
                </Col>
            </Form.Group>}
            {showPlaceHedgeToggle() &&
                <Form.Group as={Row}>
                    <Col xs="12" className="text-right">
                    <Form.Label className="primary-text font-weight-bold">Place hedge order upon offer submission</Form.Label>&nbsp;
                    <Form.Check type="switch" id="placeLimitOrder" label="" className="d-inline-block" checked={props.isPlaceHedgeOrderChecked}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => { 
                            props.setPlaceHedgeOrderChecked(event.target.checked); setUserInputPlaceHedgeToggle(event.target.checked);
                            !event.target.checked && props.onOfferUpdated({ ...props.offer, fcmAccount: null, useMini: null, numberOfContracts: null }); 
                        }}/>
                    </Col>
                </Form.Group>
                }
            {displaySendToErpToggle && activeMerchant.hasErpIntegration && !isCustomer && <Form.Group as={Row}>
                <Col xs="12" className="text-right">
                <Form.Label>Send to ERP</Form.Label>&nbsp;
                <Form.Check type="switch" id="sendToErp" label="" className="d-inline-block" checked={!props.offer.disableErpSync}
                    onChange={(ev: React.ChangeEvent<HTMLInputElement>) => { 
                        props.onOfferUpdated({...props.offer, disableErpSync: !ev.target.checked}); 
                    }}/>
                </Col>
            </Form.Group>}
        </>;
    }

interface CashPriceProps {
    value: number;
    isPricingType: boolean;
    isManualEntry: boolean;
    isMarketOrder: boolean;
    allowPriceChange: boolean;
    isCashPriceUpdated: boolean;
    priceIncrement?: number;
    min: number;
    max: number;
    isPriceLocked?: boolean;
    onValueChanged(cashPrice: number): void;
    currencyCode: string;
}

const AccountIdSelect: React.FC<AddOfferDetailsProps> = (props) => {
    return (
        <>
            <Form.Label column className="optional">Account ID</Form.Label>
            <Col xs="8">
            <TypedSelect
                options={props.offer.producer?.accounts.filter(x => x.canManage)}
                showNullRow={!props.offer.producer?.accounts}
                value={props.offer.customerAccount}
                disabled={!props.allowPickingCustomer}
                valueGetter={m => m.accountId}
                textGetter={m => getProducerAccountDisplayName(m)}
                onValueSelected={m => props.onOfferUpdated({ ...props.offer, customerAccount: m })} />
            </Col>
        </>
    );
  };

const DeliveryMethodComponent: React.FC<AddOfferDetailsProps> = props => {

    const translators = useTranslators();

    function isSetBasisOrSetFuturesOfferType(): boolean {
        return [OfferTypeEnum.FixBasisOnHta, OfferTypeEnum.FixFuturesOnBasis].includes(props.offer?.type);
    }

    return <Form.Group as={Row}>
        <Form.Label column>Delivery Method</Form.Label>
        <Col xs="8">
            {props.offer.deliveryMethod != null && (isSetBasisOrSetFuturesOfferType() || props.isPricingDelayedPriceContractModal || props.lockDeliveryMethod) ?
                <div className="col-form-label">{translators.deliveryMethods.translate(props.offer.deliveryMethod)}</div>
                : <TypedSelect
                    value={props.offer.deliveryMethod}
                    options={props.deliveryMethods}
                    disabled={props.priceAcrossLocations}
                    showNullRow
                    required
                    valueGetter={m => m}
                    textGetter={translators.deliveryMethods.translate}
                    onValueSelected={m => {
                        props.onOfferUpdated({ ...props.offer, deliveryMethod: m });
                    }} />}
        </Col>
    </Form.Group>
}

const CashPrice: React.FC<CashPriceProps> = props => {

    if (props.isPricingType || props.isManualEntry)
        return <div className="col-form-label">{`${formatDecimalPlace(props.value)} ${props.currencyCode}`}</div>;

    if (props.isMarketOrder && !props.allowPriceChange) {
        return <div className="input-group">
            <input type="text" disabled value={`${formatDecimalPlace(props.value)} ${props.currencyCode}`} className="form-control" />
            <div className="input-group-append ml-1">
                <span className="hover-text" 
                    title={localize("Details_Tooltip_CashPrice")}>
                    +/-
                </span>
            </div>
        </div>;
    }
    
    return <NumberEditor className={props.isCashPriceUpdated ? 'form-control font-weight-bold' : 'form-control'}
                disabled={props.allowPriceChange ? false: props.isMarketOrder}
                isInputLocked={props.isPriceLocked}
                required={!props.isMarketOrder}
                min={props.min}
                max={props.max}
                step={props.priceIncrement}
                allowDecimals
                precision={5}
                enforcePrecision
                roundingCent
                value={props.value}
                onValueChange={props.onValueChanged}
                unit={props.currencyCode}
            />
}

export default AddOfferDetails;